'use client'

import NextLink from 'next/link'
import { useEffect, useState } from 'react'

import { stegaClean } from 'next-sanity'

import { useDemoRequestModal } from '@/app/components/DemoRequest/DemoRequestModalContext'
import { buildTrackingData, trackElementClick, TrackingLocation } from '@/app/utils/analytics'
import { appendParams } from '@/app/utils/attribution'
import { REQUEST_DEMO_PATH } from '@/app/utils/routes'
import { cn } from '@/app/utils/style'
import { Link as LinkRaw } from '@/sanity.types'
import type { DeepRequiredNullable } from '@/types/utils'

// Legacy URL for soft launch - pages not in allowlist will link to old site
const LEGACY_URL = 'https://www.paved.com'

// Allowlist of URLs that are served from the new project
const INTERNAL_URL_ALLOWLIST = [
  '/',
  '/about',
  '/advertisers',
  '/advertiser-terms',
  '/ai',
  '/ai-connector',
  '/booker',
  '/careers',
  '/contact',
  '/developers',
  '/esp',
  '/privacy',
  '/publishers',
  '/publisher-terms',
  '/publishers/newsletter',
  '/referral-program',
  REQUEST_DEMO_PATH,
]

/**
 * Checks if a URL path is in the internal allowlist for soft launch.
 * Only URLs in the allowlist should use Next.js routing.
 */
function isInternalUrl(href: string): boolean {
  try {
    // Extract pathname from URL if it's absolute
    const url = new URL(href)
    return INTERNAL_URL_ALLOWLIST.includes(url.pathname)
  } catch {
    // Not an absolute URL, check the path directly
    const path = href.split('?')[0].split('#')[0] // Remove query and hash
    return INTERNAL_URL_ALLOWLIST.includes(path)
  }
}

/**
 * Normalizes URLs by converting same-origin absolute URLs to relative paths.
 * This prevents SSR hydration mismatches when Sanity stega encoding generates
 * absolute URLs on server but relative URLs on client.
 */
function normalizeUrl(href: string): string {
  try {
    const url = new URL(href)
    // If it's an absolute URL pointing to the current origin, make it relative
    if (typeof window !== 'undefined' && url.origin === window.location.origin) {
      return url.pathname + url.search + url.hash
    }
    // On server, check against common local origins
    if (
      typeof window === 'undefined' &&
      (url.origin === 'http://localhost:3000' || url.origin === 'http://localhost:3333')
    ) {
      return url.pathname + url.search + url.hash
    }
    return href
  } catch {
    // Not a valid absolute URL, return as-is
    return href
  }
}

type LinkWithNulls = DeepRequiredNullable<LinkRaw>

type LinkWithDeref = Omit<LinkWithNulls, 'internalPage' | 'internalPost'> & {
  href?: string | null
  internalPage?: {
    _id?: string
    _type?: string
    slug?: string | null
    name?: string | null
    title?: string | null
  } | null
  internalPost?: { _id?: string; slug?: string | null; title?: string | null } | null
}

export type LinkProps = React.ComponentProps<'a'> & {
  link: LinkWithDeref
  tracking: TrackingLocation
  className?: string
  children?: React.ReactNode
}

/**
 * Resolves the display label for a link by checking multiple properties in order:
 * 1. Explicit label
 * 2. Internal page name
 * 3. Internal page title
 * 4. Internal post title
 * 5. Empty string
 */
export function getLinkLabel(link: LinkWithDeref | null | undefined): string {
  if (!link) return ''

  return (
    link.label ||
    link.internalPage?.name ||
    link.internalPage?.title ||
    link.internalPost?.title ||
    ''
  )
}

export const Link = function ({ link, tracking, className, children, ...anchorProps }: LinkProps) {
  const { openModal } = useDemoRequestModal()

  // Clean stega encoding from entire Sanity object before using in useEffect
  const cleanLink = link ? stegaClean(link) : null

  // Pre-compute values the hooks below depend on. These are derived from
  // cleanLink with null-safe fallbacks so the hooks fire unconditionally —
  // the actual early bail is just below. When cleanLink is null the values
  // are harmless defaults; the component returns null before they're used.
  const isExternalLink = cleanLink?.linkType === 'external'
  const isDemoPopup = cleanLink?.linkType === 'demoPopup'
  const normalizedHref = cleanLink?.href ? normalizeUrl(cleanLink.href) : ''
  const hasInternalRelation = !!(cleanLink?.internalPage || cleanLink?.internalPost)
  const isAllowlisted = !!normalizedHref && isInternalUrl(normalizedHref)
  const shouldUseLegacyUrl = hasInternalRelation && !!normalizedHref && !isAllowlisted

  const baseFinalHref = isDemoPopup
    ? '#'
    : shouldUseLegacyUrl
      ? `${LEGACY_URL}${normalizedHref}`
      : isExternalLink && cleanLink?.href
        ? cleanLink.href // Preserve original absolute URL for external links to prevent SPA navigation
        : normalizedHref

  // Append captured attribution params (utm_*, gclid) to outbound URLs going
  // to legacy Rails-served paths so the Rails app can set its signed attribution
  // cookies. Done in an effect to avoid SSR/CSR hydration mismatch — sessionStorage
  // is only available after mount.
  const isLegacyTarget =
    shouldUseLegacyUrl ||
    (isExternalLink &&
      !!cleanLink?.href &&
      cleanLink.href.startsWith(LEGACY_URL) &&
      !isInternalUrl(cleanLink.href))
  const [finalHref, setFinalHref] = useState(baseFinalHref)
  useEffect(() => {
    setFinalHref(isLegacyTarget ? appendParams(baseFinalHref) : baseFinalHref)
  }, [baseFinalHref, isLegacyTarget])

  if (!link || !cleanLink) return null

  const label = getLinkLabel(cleanLink)
  const ariaLabel = cleanLink.ariaLabel || label
  const openInNewTab = !!cleanLink.openInNewTab

  // Use children if provided, otherwise fall back to label
  const content = children !== undefined ? children : label
  const textContent = typeof content === 'string' ? content : label

  const finalClassName = cn('no-underline hover:no-underline', className)

  // Handle click tracking and prevent default for demoPopup
  const handleClick = (e: React.MouseEvent) => {
    if (isDemoPopup) {
      e.preventDefault()
      openModal()
    }

    // Handle smooth scroll for anchor links
    const isAnchorLink = finalHref.startsWith('#')
    if (isAnchorLink && finalHref !== '#') {
      e.preventDefault()
      const targetId = finalHref.substring(1)
      const targetElement = document.getElementById(targetId)
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
        // Update URL hash without jumping
        window.history.pushState(null, '', finalHref)
      }
    }

    const trackingData = buildTrackingData('link', textContent, tracking, finalHref || '')
    trackElementClick(trackingData)
  }

  if (!finalHref) {
    return (
      <span className={finalClassName} aria-label={ariaLabel} {...(anchorProps as any)}>
        {content}
      </span>
    )
  }

  const isAnchorLink = finalHref.startsWith('#')
  const useRegularAnchor =
    !isAnchorLink &&
    (shouldUseLegacyUrl ||
      isExternalLink ||
      finalHref.startsWith('mailto:') ||
      finalHref.startsWith('tel:'))

  const rel = openInNewTab ? 'noopener noreferrer' : undefined
  const target = openInNewTab ? '_blank' : undefined

  if (useRegularAnchor || isDemoPopup) {
    return (
      <a
        href={finalHref}
        className={finalClassName}
        aria-label={ariaLabel}
        rel={rel}
        target={target}
        onClick={handleClick}
        {...anchorProps}
      >
        {content}
      </a>
    )
  }

  // Use Next.js Link for internal routes and anchor links
  return (
    <NextLink
      href={finalHref}
      className={finalClassName}
      aria-label={ariaLabel}
      rel={rel}
      target={target}
      onClick={handleClick}
      {...anchorProps}
    >
      {content}
    </NextLink>
  )
}
